import { useCallback } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Small } from 'foundations-components/transitional/components/Small';
import { AccordionItem } from 'foundations-components/transitional/components/AccordionItem';
import { CogIcon } from 'foundations-assets/icons/components/CogIcon';
import { AiMessageIcon } from 'foundations-assets/icons/components/AiMessageIcon';
import { Tag } from 'foundations-components/transitional/components/Tag';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DefaultToolCall } from './ToolCallGroup';
import { AskUserQuestion } from './AskUserQuestion';
import { ASK_USER_QUESTION_TOOL_NAME, ASK_USER_QUESTIONS_TOOL_NAME, normalizeQuestions, parseQuestionResponses } from 'breeze-engine-core/askUserQuestionSpec';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SUBAGENT_TOOL_NAME = 'task';
const MAX_SUBAGENT_DEPTH = 2;
function getToolInputRecord(input) {
  if (input != null && typeof input === 'object' && !Array.isArray(input)) {
    return input;
  }
  return null;
}
function getSubagentName(pair) {
  var _pair$subagentMessage, _pair$subagentMessage2;
  const input = getToolInputRecord(pair.input.input);
  if (input && typeof input.subagentType === 'string') {
    return input.subagentType;
  }
  return (_pair$subagentMessage = (_pair$subagentMessage2 = pair.subagentMessages) === null || _pair$subagentMessage2 === void 0 || (_pair$subagentMessage2 = _pair$subagentMessage2[0]) === null || _pair$subagentMessage2 === void 0 || (_pair$subagentMessage2 = _pair$subagentMessage2.subagentSource) === null || _pair$subagentMessage2 === void 0 ? void 0 : _pair$subagentMessage2.subagentName) !== null && _pair$subagentMessage !== void 0 ? _pair$subagentMessage : null;
}
function SubagentCallBlock({
  pair,
  renderBlock,
  depth = 0
}) {
  const messages = pair.subagentMessages;
  const name = getSubagentName(pair);
  const renderToolBlock = useCallback((toolBlock, key) => {
    const nextDepth = depth + 1;
    return /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "200",
      children: toolBlock.pairs.map(toolPair => {
        const isNestedSubagent = toolPair.input.name === SUBAGENT_TOOL_NAME && toolPair.subagentMessages && toolPair.subagentMessages.length > 0;
        if (isNestedSubagent && nextDepth < MAX_SUBAGENT_DEPTH) {
          return /*#__PURE__*/_jsx(SubagentCallBlock, {
            pair: toolPair,
            renderBlock: renderBlock,
            depth: nextDepth
          }, toolPair.input.id);
        }
        return /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          gap: "200",
          children: /*#__PURE__*/_jsx(DefaultToolCall, {
            pair: toolPair
          })
        }, toolPair.input.id);
      })
    }, key);
  }, [depth, renderBlock]);
  const renderContent = useCallback(() => {
    return /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "200",
      padding: "300",
      children: messages.map(msg => /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        gap: "100",
        children: msg.blocks.map((subBlock, blockIndex) => {
          const blockKey = `${msg.id}-${blockIndex}`;
          if (subBlock.type === 'TOOL') {
            return renderToolBlock(subBlock, blockKey);
          }
          return renderBlock(subBlock, blockKey);
        })
      }, msg.id))
    });
  }, [messages, renderToolBlock, renderBlock]);
  return /*#__PURE__*/_jsx("div", {
    "data-block-type": "subagent-call",
    children: /*#__PURE__*/_jsx(AccordionItem, {
      size: "extra-small",
      flush: true,
      animate: true,
      defaultOpen: false,
      removeContentPadding: true,
      renderContent: renderContent,
      title: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "100",
        children: [/*#__PURE__*/_jsx(AiMessageIcon, {
          size: "300"
        }), /*#__PURE__*/_jsx(Tag, {
          use: "badge-pantera",
          inline: true,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "breezeEngineChatComponents.subagent.label"
          })
        }), name && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Small, {
            use: "help",
            children: "\xB7"
          }), /*#__PURE__*/_jsx(Small, {
            weight: "demi",
            children: name
          })]
        })]
      })
    })
  });
}
function SubagentPanel({
  messages,
  renderBlock
}) {
  var _messages$0$subagentS;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "300",
    padding: "300",
    borderWidth: "100",
    borderStyle: "solid",
    borderColor: "border-core-subtle",
    borderRadius: "200",
    backgroundColor: "fill-surface-default",
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "200",
      paddingBlockEnd: "200",
      borderBlockEndWidth: "100",
      borderBlockEndStyle: "solid",
      borderBlockEndColor: "border-core-subtle",
      children: [/*#__PURE__*/_jsx(CogIcon, {
        size: "200"
      }), /*#__PURE__*/_jsx(Small, {
        weight: "demi",
        children: ((_messages$0$subagentS = messages[0].subagentSource) === null || _messages$0$subagentS === void 0 ? void 0 : _messages$0$subagentS.subagentName) || 'Subagent'
      })]
    }), messages.map(msg => /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "100",
      children: msg.blocks.map((subBlock, blockIndex) => renderBlock(subBlock, `${msg.id}-${blockIndex}`))
    }, msg.id))]
  });
}
export function ToolBlockRenderer({
  block,
  renderBlock
}) {
  const {
    pairs
  } = block;
  return /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    gap: "200",
    children: pairs.map(pair => {
      if (pair.input.name === ASK_USER_QUESTION_TOOL_NAME) {
        var _pair$output;
        const questionItem = pair.input.input;
        const isPending = !pair.output;
        let submittedResponse;
        if (((_pair$output = pair.output) === null || _pair$output === void 0 ? void 0 : _pair$output.type) === 'TEXT' && !pair.output.isError) {
          const parsed = parseQuestionResponses(pair.output.content);
          submittedResponse = parsed === null || parsed === void 0 ? void 0 : parsed[0];
        }
        return /*#__PURE__*/_jsx(AskUserQuestion, {
          question: questionItem,
          isPending: isPending,
          submittedResponse: isPending ? undefined : submittedResponse
        }, pair.input.id);
      }
      if (pair.input.name === ASK_USER_QUESTIONS_TOOL_NAME) {
        var _pair$output2;
        const input = pair.input.input;
        const questions = normalizeQuestions(input);
        const isPending = !pair.output;
        let submittedResponses;
        if (((_pair$output2 = pair.output) === null || _pair$output2 === void 0 ? void 0 : _pair$output2.type) === 'TEXT' && !pair.output.isError) {
          submittedResponses = parseQuestionResponses(pair.output.content);
        }
        return /*#__PURE__*/_jsx(AskUserQuestion, {
          questions: questions,
          isPending: isPending,
          submittedResponses: isPending ? undefined : submittedResponses
        }, pair.input.id);
      }
      const isSubagentCall = pair.input.name === SUBAGENT_TOOL_NAME && pair.subagentMessages && pair.subagentMessages.length > 0;
      if (isSubagentCall && renderBlock) {
        return /*#__PURE__*/_jsx(SubagentCallBlock, {
          pair: pair,
          renderBlock: renderBlock
        }, pair.input.id);
      }
      return /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "200",
        children: [/*#__PURE__*/_jsx(DefaultToolCall, {
          pair: pair
        }), renderBlock && pair.subagentMessages && pair.subagentMessages.length > 0 && /*#__PURE__*/_jsx(SubagentPanel, {
          messages: pair.subagentMessages,
          renderBlock: renderBlock
        })]
      }, pair.input.id);
    })
  });
}